import React, { useEffect, useState } from "react";
import Popper from "@mui/material/Popper";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectGOGroups } from "store/selectors";
import { fetchGOGroups } from "store/appSlice";
import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Grow,
  IconButton,
  Link,
  MenuItem,
  MenuList,
  Modal,
  Paper,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import Table from "components/Table";
import NewGOGroupModal from "components/Modals/NewGOGroup";
import EditGOGroupModal from "components/Modals/EditGOGroup";
import { deleteGOGroup } from "api/resources/go";
import { ProtectedPaths } from "routes";

const GOGroupsTable = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [editModal, setEditModal] = useState(null);

  const dispatch = useAppDispatch();
  const GOGroups = useAppSelector(selectGOGroups);

  useEffect(() => {
    dispatch(fetchGOGroups());
  }, []);

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
    },
    {
      accessorKey: "clients",
      header: "Clients",
      cell: ({ row }: { row: { original: any } }) => {
        const linkedClients = row.original.clients;
        return (
          <>
            {linkedClients
              .slice(0, 2)
              .map((value: { id: number; name: string }) => (
                <Link
                  to={`/${ProtectedPaths.Clients}/${value.id}`}
                  key={`ClientChip-${value.id}`}
                >
                  <Chip
                    label={value.name}
                    size="small"
                    sx={{ mr: 1 }}
                    clickable
                  />
                </Link>
              ))}
            {linkedClients.length > 2 && (
              <Chip
                label={`+ ${linkedClients.length - 2}`}
                size="small"
                sx={{ mr: 1 }}
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: "settings",
      header: "",
      cell: (row: any) => {
        const [open, setOpen] = React.useState(false);
        const [anchorElDelete, setAnchorElDelete] = React.useState(null);
        const anchorRef = React.useRef<HTMLButtonElement>(null);

        const handleClickDelete = (event) => {
          setAnchorElDelete(event.currentTarget);
        };

        const handleCloseDelete = () => setAnchorElDelete(null);

        const openDelete = Boolean(anchorElDelete);

        const handleToggle = () => {
          setOpen((prevOpen) => !prevOpen);
        };

        const handleClose = (event: Event | React.SyntheticEvent) => {
          if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
          ) {
            return;
          }

          setOpen(false);
        };

        function handleListKeyDown(event: React.KeyboardEvent) {
          if (event.key === "Tab") {
            event.preventDefault();
            setOpen(false);
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }

        // return focus to the button when we transitioned from !open -> open
        const prevOpen = React.useRef(open);
        React.useEffect(() => {
          if (prevOpen.current === true && open === false) {
            anchorRef.current!.focus();
          }

          prevOpen.current = open;
        }, [open]);

        const handleDeleteGOGroup = (id: number) => {
          const postData = {
            clientGroupId: id,
          };

          deleteGOGroup(postData)
            .then(() => {
              dispatch(fetchGOGroups());
              handleCloseDelete();
            })
            .catch((error) => {
              console.error(error);
            });
        };

        return (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton
              ref={anchorRef}
              id="composition-button"
              aria-controls={open ? "composition-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              placement="right-start"
              transition
              disablePortal
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: "right top",
                  }}
                >
                  <Paper style={{ marginRight: "1rem" }}>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList
                        autoFocusItem={open}
                        id="composition-menu"
                        aria-labelledby="composition-button"
                        onKeyDown={handleListKeyDown}
                      >
                        <MenuItem
                          onClick={() => setEditModal(row.row.original)}
                        >
                          Edit
                        </MenuItem>
                        <MenuItem onClick={handleClickDelete}>Delete</MenuItem>
                        <Popover
                          open={openDelete}
                          anchorEl={anchorElDelete}
                          onClose={handleCloseDelete}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          slotProps={{
                            paper: {
                              sx: { mt: 2 }, // adds 8px gap
                            },
                          }}
                        >
                          <div style={{ padding: "1rem" }}>
                            <Typography>Are you sure?</Typography>
                            <Stack direction="row" spacing={1} mt={1}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() =>
                                  handleDeleteGOGroup(row.row.original.id)
                                }
                              >
                                Yes, delete
                              </Button>
                              <Button size="small" onClick={handleCloseDelete}>
                                Cancel
                              </Button>
                            </Stack>
                          </div>
                        </Popover>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">GO groups</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
        >
          Add group
        </Button>
      </Box>
      <Table
        data={GOGroups}
        columns={columns}
        isFetching={!GOGroups}
        pageCount={1}
        page={setCurrentPage}
        searchLabel="Otsi kasutajat"
      />
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <NewGOGroupModal
          setOpenModal={setOpenModal}
          dispatch={() => dispatch(fetchGOGroups())}
        />
      </Modal>
      <Modal open={editModal} onClose={() => setEditModal(false)}>
        <EditGOGroupModal
          group={editModal}
          setEditModal={setEditModal}
          dispatch={() => dispatch(fetchGOGroups())}
        />
      </Modal>
    </>
  );
};

export default GOGroupsTable;
